import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import Apirest from "../../Content/Apirest";
import { UserInfo } from '../../interfaces/user';
import history from '../../routerHistory';
import SiteMap from '../../Commons/SiteMap';

import { StoreState } from '../../store';
import { SET_USER_INFO } from "../../store/user/types";
import { SET_CONTENT, SET_LOCALE, SET_LOCAL_CODE, DELETE_CONTENT } from "../../store/i18n/types";
import {
    getSessionLanguage, localCodeFromLocale, resolveLocale
} from "../../Commons/SessionLanguage";
import CMS from "../../Content/cms";
import moment from 'moment';
import 'moment/locale/es';
import 'moment/locale/en-ca';
import 'moment/locale/pt';
import 'moment/locale/fr';

import LoaderSpinner from "../../Commons/Components/LoaderSpinner";
import Impersonification from '../Impersonification';

const UserConfig: React.FC = ({ children }) => {
    const [isUserLoaded, setIsUserLoaded] = useState(false);
    const [showImpersonModal, setShowImpersonModal] = useState(true);

    const user = useSelector((state: StoreState) => state.user);
    const locale = useSelector((state: StoreState) => state.i18n.locale);
    const content = useSelector((state: StoreState) => state.i18n.content);
    const dispatch = useDispatch();

    useEffect(() => {
        Apirest.get_user_info((response: UserInfo) => {
            dispatch({ type: SET_USER_INFO, payload: response });
            setIsUserLoaded(true);
        });
    }, [dispatch]);

    // The language selected for the session wins over the account one, which is
    // only the fallback when nothing was selected.
    useEffect(() => {
        if (!user.info) return;

        const accountLocale = user.info.locale.code;
        const sessionLanguage = getSessionLanguage();

        dispatch({
            type: SET_LOCAL_CODE,
            payload: sessionLanguage ?? localCodeFromLocale(accountLocale)
        });

        dispatch({
            type: SET_LOCALE,
            payload: sessionLanguage ?
                resolveLocale(sessionLanguage, accountLocale) :
                accountLocale
        });
    }, [user.info, dispatch]);

    useEffect(() => {
        if (!locale) return;

        dispatch({ type: DELETE_CONTENT });

        if (locale.startsWith('es-')) moment.locale('es');
        else if (locale.startsWith('en-')) moment.locale('en-ca');
        else if (locale.startsWith('pt-')) moment.locale('pt');
        else if (locale.startsWith('fr-')) moment.locale('fr');
        else moment.locale('en-ca');

        CMS.get_content(locale, (response) => {
            dispatch({ type: SET_CONTENT, payload: response });
        });

        document.documentElement.lang = locale;
    }, [locale, dispatch]);

    if (!content || !isUserLoaded) {
        return <LoaderSpinner />;
    }

    const { info } = user;
    const isExaminer = info?.roles?.is_examiner || info?.is_impersonifing;
    const isAdmin = info?.roles?.is_manager || info?.roles?.is_owner;
    const isAdminPage = window.location.pathname === SiteMap.getFullURL('admin');

    if (isExaminer || (isAdmin && isAdminPage)) {
        return <>{children}</>;
    }

    if (isAdmin && !isAdminPage) {
        history.push(SiteMap.getFullURL('admin'));
        return null;
    }

    return (
        <Impersonification
            show={showImpersonModal}
            onHide={() => setShowImpersonModal(false)}
            disable_close_action
        />
    );
};

export default UserConfig;
